import apiClient from "@/lib/api/apiClient";
import { showSuccess, showError } from "@/lib/utils/notifications";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { ValidationModal } from "@/components/ui/ValidationModal";
import { dispatchAboutYourselfRevealInlineErrors } from "./tabs/aboutYourselfTabHelpers";
import { NewAgreementModal } from "@/components/modals/NewAgreementModal";
import { NoEmploymentModal } from "@/features/applicant/components/NoEmploymentModal";
import { ConfirmationModal } from "./modals/ConfirmationModal";
import { PaymentAdjustmentModal } from "./modals/PaymentAdjustmentModal";
import { generateChargebackPacket } from "@/lib/api/paymentLogService";
import { PaymentProcessAdjustmentModal } from "./modals/PaymentProcessAdjustmentModal";
import { RiskRatingAdjustmentModal } from "./modals/RiskRatingAdjustmentModal";
import { MaxRentHistoryModal } from "./modals/MaxRentHistoryModal";
import { PolicyDeclarationFilesModal } from "./modals/PolicyDeclarationFilesModal";
import type {
  PaymentAdjustmentApplicationContext,
  PaymentProcessApplication,
} from "../types/modals";
import type { ApplicationDetailModalsProps } from "../types";

export function ApplicationDetailModals({
  applicantId,
  applicationAllData,
  applicationData,
  selectedTab,
  showImpersonateModal,
  leaseStartDateAdjustConfirmOpen,
  leaseDetailsChangedConfirmOpen,
  reimbursementApplicationDetailsConfirmOpen,
  tabChangeSaveConfirm,
  tabChangeInvalidConfirm,
  rentAdjustmentModal,
  paymentProcessModalOpen,
  showRiskRatingModal,
  showMaxRentModal,
  maxRentHistoryModalKey,
  policyDeclarationModalOpen,
  policyDeclarationFiles,
  reimbursementExplanationOpen,
  validationModal,
  sharedPhoneErrorOpen,
  addressErrorOpen,
  noEmploymentModalOpen,
  importPreviousAddressOpen,
  importPreviousEmploymentOpen,
  updateButtonRef,
  pendingTabAfterSaveRef,
  saveModeRef,
  leaseChangeActionRef,
  submitWithoutLeaseDateUpdateRef,
  noEmploymentConfirmedRef,
  setShowImpersonateModal,
  setLeaseStartDateAdjustConfirmOpen,
  setLeaseDetailsChangedConfirmOpen,
  setReimbursementApplicationDetailsConfirmOpen,
  setTabChangeSaveConfirm,
  setTabChangeInvalidConfirm,
  setRentAdjustmentModal,
  setPaymentProcessModalOpen,
  setShowRiskRatingModal,
  setShowMaxRentModal,
  setPolicyDeclarationModalOpen,
  setReimbursementExplanationOpen,
  setValidationModal,
  setSharedPhoneErrorOpen,
  setAddressErrorOpen,
  setNoEmploymentModalOpen,
  setImportPreviousAddressOpen,
  setAboutYourselfExposeInlineValidation,
  setPropertyExposeInlineValidation,
  setIncomeExposeInlineValidation,
  setFormData,
  setDirtyTabs,
  setSelectedTab,
  setUpdateTriggerCounter,
  performImpersonate,
  refreshApplicationDetail,
  importPreviousAddresses,
  setImportPreviousDismissed,
  importPreviousEmployment,
  dismissImportPreviousEmployment,
}: ApplicationDetailModalsProps) {
  return (
    <>
      <ConfirmDialog
        isOpen={leaseStartDateAdjustConfirmOpen}
        title="Lease Start Date"
        message="Does the Lease Start Date need to be Adjusted into the future?"
        primaryLabel="Yes"
        secondaryLabel="No"
        primaryFirst
        presentation="saveChanges"
        actionStyle="application"
        overlayDataTestId="application-detail-lease-start-date-adjust-modal"
        primaryButtonDataTestId="application-detail-lease-start-date-adjust-yes-button"
        secondaryButtonDataTestId="application-detail-lease-start-date-adjust-no-button"
        closeButtonDataTestId="application-detail-lease-start-date-adjust-close-button"
        onPrimary={() => {
          setLeaseStartDateAdjustConfirmOpen(false);
          submitWithoutLeaseDateUpdateRef.current = false;
          const leaseStartInput = document.querySelector<HTMLElement>(
            '[data-testid="application-property-tab-lease-start-date-input"] input',
          );
          leaseStartInput?.focus();
          leaseStartInput?.scrollIntoView({ behavior: "smooth", block: "center" });
        }}
        onSecondary={() => {
          setLeaseStartDateAdjustConfirmOpen(false);
          submitWithoutLeaseDateUpdateRef.current = true;
          setUpdateTriggerCounter((n) => n + 1);
        }}
        onDismiss={() => {
          setLeaseStartDateAdjustConfirmOpen(false);
          submitWithoutLeaseDateUpdateRef.current = false;
        }}
      />

      <ConfirmDialog
        isOpen={leaseDetailsChangedConfirmOpen}
        title="Application Details Changed"
        message="Lease related details have changed. Do you want to resend an email to the property reflecting these details?"
        primaryLabel="Email the Property"
        neutralLabel="Save Without Notification"
        secondaryLabel="Cancel"
        primaryFirst
        presentation="saveChanges"
        actionStyle="application"
        size="wide"
        equalWidthActions
        overlayDataTestId="application-detail-lease-details-changed-modal"
        primaryButtonDataTestId="application-detail-lease-details-email-property-button"
        neutralButtonDataTestId="application-detail-lease-details-save-without-notification-button"
        secondaryButtonDataTestId="application-detail-lease-details-cancel-button"
        closeButtonDataTestId="application-detail-lease-details-close-button"
        onPrimary={() => {
          setLeaseDetailsChangedConfirmOpen(false);
          leaseChangeActionRef.current = "email_property";
          setUpdateTriggerCounter((n) => n + 1);
        }}
        onNeutral={() => {
          setLeaseDetailsChangedConfirmOpen(false);
          leaseChangeActionRef.current = "save_without_notification";
          setUpdateTriggerCounter((n) => n + 1);
        }}
        onSecondary={() => {
          setLeaseDetailsChangedConfirmOpen(false);
          leaseChangeActionRef.current = null;
          submitWithoutLeaseDateUpdateRef.current = false;
          pendingTabAfterSaveRef.current = null;
          saveModeRef.current = "explicitUpdate";
        }}
        onDismiss={() => {
          setLeaseDetailsChangedConfirmOpen(false);
          leaseChangeActionRef.current = null;
          submitWithoutLeaseDateUpdateRef.current = false;
          pendingTabAfterSaveRef.current = null;
          saveModeRef.current = "explicitUpdate";
        }}
      />

      <ConfirmDialog
        isOpen={reimbursementApplicationDetailsConfirmOpen}
        title="Reimbursement Request"
        message="A Reimbursement request exists for this Resident so a new copy of Resident Access Confirmation Email will not be created."
        primaryLabel="Ok"
        secondaryLabel=""
        overlayDataTestId="application-detail-reimbursement-request-modal"
        primaryButtonDataTestId="application-detail-reimbursement-request-ok-button"
        closeButtonDataTestId="application-detail-reimbursement-request-close-button"
        onPrimary={() => {
          setReimbursementApplicationDetailsConfirmOpen(false);
          leaseChangeActionRef.current = "save_without_notification";
          setUpdateTriggerCounter((n) => n + 1);
        }}
        onSecondary={() => {
          setReimbursementApplicationDetailsConfirmOpen(false);
          leaseChangeActionRef.current = null;
          submitWithoutLeaseDateUpdateRef.current = false;
          pendingTabAfterSaveRef.current = null;
          saveModeRef.current = "explicitUpdate";
        }}
        onDismiss={() => {
          setReimbursementApplicationDetailsConfirmOpen(false);
          leaseChangeActionRef.current = null;
          submitWithoutLeaseDateUpdateRef.current = false;
          pendingTabAfterSaveRef.current = null;
          saveModeRef.current = "explicitUpdate";
        }}
      />

      <ConfirmDialog
        isOpen={tabChangeSaveConfirm.open}
        title="Save Changes"
        message="Do you want to save your updates?"
        primaryLabel="Yes"
        secondaryLabel="No"
        primaryFirst
        presentation="saveChanges"
        overlayDataTestId="application-detail-confirm-tab-save-changes"
        primaryButtonDataTestId="application-detail-confirm-tab-save-changes-primary-button"
        secondaryButtonDataTestId="application-detail-confirm-tab-save-changes-secondary-button"
        closeButtonDataTestId="application-detail-confirm-tab-save-changes-close-button"
        onPrimary={() => {
          const target = tabChangeSaveConfirm.targetTab;
          setTabChangeSaveConfirm({ open: false, targetTab: null });
          if (target) pendingTabAfterSaveRef.current = target;
          saveModeRef.current = "tabChangeSave";
          updateButtonRef.current?.click();
        }}
        onSecondary={() => {
          const target = tabChangeSaveConfirm.targetTab;
          setTabChangeSaveConfirm({ open: false, targetTab: null });
          setFormData({});
          setDirtyTabs((prev) => ({ ...prev, [selectedTab]: false }));
          if (target) setSelectedTab(target);
        }}
      />

      <ConfirmDialog
        isOpen={tabChangeInvalidConfirm.open}
        title="Validation"
        message={tabChangeInvalidConfirm.message}
        primaryLabel="Continue"
        secondaryLabel="Cancel"
        presentation="validationReview"
        overlayDataTestId="application-detail-confirm-tab-validation"
        primaryButtonDataTestId="application-detail-confirm-tab-validation-primary-button"
        secondaryButtonDataTestId="application-detail-confirm-tab-validation-secondary-button"
        closeButtonDataTestId="application-detail-confirm-tab-validation-close-button"
        onPrimary={() => {
          const target = tabChangeInvalidConfirm.targetTab;
          setTabChangeInvalidConfirm({ open: false, targetTab: null, message: "" });
          if (target) setSelectedTab(target);
        }}
        onSecondary={() => {
          setTabChangeInvalidConfirm({ open: false, targetTab: null, message: "" });
          if (selectedTab === "aboutyourselfinfo") {
            setAboutYourselfExposeInlineValidation(true);
            dispatchAboutYourselfRevealInlineErrors();
          }
          if (selectedTab === "propertyinfo") {
            setPropertyExposeInlineValidation(true);
          }
          if (selectedTab === "employmentinfo") {
            setIncomeExposeInlineValidation(true);
            window.dispatchEvent(
              new CustomEvent("liberty:income-employment:validate"),
            );
          }
        }}
      />

      {showImpersonateModal && (
        <ConfirmationModal
          title="Confirm Impersonation Request"
          message="For best results, all other Cornerstone tabs should be closed before proceeding with impersonating."
          positiveTitle="OK"
          negativeTitle="Cancel"
          testIdPrefix="application-detail-confirm-impersonate"
          onConfirm={() => {
            setShowImpersonateModal(false);
            void performImpersonate();
          }}
          onCancel={() => setShowImpersonateModal(false)}
          onClose={() => setShowImpersonateModal(false)}
        />
      )}

      {paymentProcessModalOpen && (
        <PaymentProcessAdjustmentModal
          isOpen={paymentProcessModalOpen}
          onClose={() => setPaymentProcessModalOpen(false)}
          application={applicationAllData as PaymentProcessApplication}
          applicantId={applicantId}
          onSubmit={async (data) => {
            try {
              const { paymentAdjustment, ...residentApplicationData } = data;
              const coResident =
                applicationAllData?.co_resident_application?.[String(applicantId)] ||
                applicationData;
              await apiClient.post(`/api/resident_application/${applicantId}`, {
                ...residentApplicationData,
                other_individuals: Number(
                  coResident?.other_individuals ||
                    residentApplicationData.other_individuals ||
                    0,
                ),
              });
              if (paymentAdjustment) {
                await apiClient.post(
                  `/api/payment_refund/${paymentAdjustment.payment_log_id}`,
                  paymentAdjustment.requestData,
                );
              }
              showSuccess("Property info updated successfully!");
              await refreshApplicationDetail();
            } catch (err: unknown) {
              showError(
                (err as { error?: string })?.error ||
                  (err instanceof Error ? err.message : "") ||
                  "Failed to update property info",
              );
              throw err;
            }
          }}
        />
      )}

      {rentAdjustmentModal.show && (
        <PaymentAdjustmentModal
          isOpen={rentAdjustmentModal.show}
          onClose={() =>
            setRentAdjustmentModal({
              show: false,
              payment: null,
              autoSelectedData: null,
            })
          }
          payment={rentAdjustmentModal.payment}
          application={applicationAllData as PaymentAdjustmentApplicationContext}
          autoSelectedData={rentAdjustmentModal.autoSelectedData || undefined}
          onSubmit={async (data) => {
            const paymentLogId = rentAdjustmentModal.payment?.payment_log_id;
            if (paymentLogId == null) return;
            try {
              await apiClient.post(`/api/payment_refund/${paymentLogId}`, {
                adjustment_type: data.adjustment_type,
                adjustment_reason: data.adjustment_reason || "",
                refund_amount: data.refund_amount,
                notes: data.notes || "",
                adjustment_data: data.adjustment_data || {},
              });
              showSuccess("Payment Adjustment Added Successfully!");
              const chainRisk = rentAdjustmentModal.afterRiskRating;
              setRentAdjustmentModal({
                show: false,
                payment: null,
                autoSelectedData: null,
              });
              if (chainRisk) {
                setShowRiskRatingModal(true);
              } else {
                window.location.reload();
              }
            } catch (err: unknown) {
              showError(
                (err as { error?: string })?.error ||
                  (err instanceof Error ? err.message : "") ||
                  "Failed to process adjustment",
              );
            }
          }}
          onGenerateChargebackPacket={async ({ applicantId, paymentLogId }) => {
            try {
              await generateChargebackPacket(applicantId, paymentLogId);
              showSuccess("Chargeback Packet Generated Sucessfully!");
              setRentAdjustmentModal({
                show: false,
                payment: null,
                autoSelectedData: null,
              });
              window.location.reload();
            } catch (err: unknown) {
              showError(
                (err as { error?: string })?.error ||
                  (err instanceof Error ? err.message : "") ||
                  "Failed to generate chargeback packet",
              );
            }
          }}
        />
      )}

      {showRiskRatingModal && (
        <RiskRatingAdjustmentModal
          isOpen={showRiskRatingModal}
          onClose={() => setShowRiskRatingModal(false)}
          applicantId={applicantId}
          applicationAllData={applicationAllData}
          onSuccess={() => void refreshApplicationDetail()}
        />
      )}

      <MaxRentHistoryModal
        key={maxRentHistoryModalKey}
        isOpen={showMaxRentModal}
        onClose={() => setShowMaxRentModal(false)}
        applicationId={applicationAllData.application_id}
      />

      <PolicyDeclarationFilesModal
        isOpen={policyDeclarationModalOpen}
        onClose={() => setPolicyDeclarationModalOpen(false)}
        files={policyDeclarationFiles}
      />

      <NewAgreementModal
        isOpen={reimbursementExplanationOpen}
        onClose={() => setReimbursementExplanationOpen(false)}
      />

      <ValidationModal
        isOpen={validationModal.open}
        title={validationModal.title}
        message={validationModal.message}
        alignMessage
        showValidationIcon={validationModal.showValidationIcon}
        onClose={() => {
          setValidationModal({ open: false, title: "", message: "" });
          if (selectedTab === "aboutyourselfinfo") {
            dispatchAboutYourselfRevealInlineErrors();
          }
        }}
        overlayDataTestId="application-detail-validation-modal"
        closeButtonDataTestId="application-detail-validation-modal-close-button"
        confirmButtonDataTestId="application-detail-validation-modal-confirm-button"
      />

      <ValidationModal
        isOpen={sharedPhoneErrorOpen}
        title="Mobile Number Already Registered"
        message="The phone number is already in use by another user. If this is a shared phone number (e.g., shared with a spouse), please check the 'This is a shared phone#' box above to proceed."
        alignMessage
        onClose={() => setSharedPhoneErrorOpen(false)}
        overlayDataTestId="application-detail-shared-phone-error-modal"
        closeButtonDataTestId="application-detail-shared-phone-error-modal-close-button"
        confirmButtonDataTestId="application-detail-shared-phone-error-modal-confirm-button"
      />

      <ValidationModal
        isOpen={addressErrorOpen}
        title="Missing Fields"
        message={
          "The address you entered is outside of the USA. Please add an alternate US address.\n" +
          'If you don\'t have an alternate, US-based address, you can still continue by checking the "I don\'t have an alternate address in the US" box.'
        }
        alignMessage
        onClose={() => setAddressErrorOpen(false)}
        overlayDataTestId="application-detail-address-error-modal"
        closeButtonDataTestId="application-detail-address-error-modal-close-button"
        confirmButtonDataTestId="application-detail-address-error-modal-confirm-button"
      />

      <NoEmploymentModal
        isOpen={noEmploymentModalOpen}
        onClose={() => setNoEmploymentModalOpen(false)}
        onConfirm={() => {
          noEmploymentConfirmedRef.current = true;
          setNoEmploymentModalOpen(false);
          setUpdateTriggerCounter((n) => n + 1);
        }}
      />

      <ConfirmDialog
        isOpen={importPreviousAddressOpen}
        title="Import from Previous Application"
        message="We found some address records from your previous application with us. Would you like us to import these?"
        primaryLabel="Yes, import as previous address"
        overlayDataTestId="application-detail-confirm-import-previous-address"
        primaryButtonDataTestId="application-detail-confirm-import-previous-address-primary-button"
        secondaryButtonDataTestId="application-detail-confirm-import-previous-address-secondary-button"
        closeButtonDataTestId="application-detail-confirm-import-previous-address-close-button"
        onPrimary={importPreviousAddresses}
        secondaryLabel="No, thank you"
        onSecondary={() => {
          setImportPreviousAddressOpen(false);
          setImportPreviousDismissed(true);
        }}
        onDismiss={() => {
          setImportPreviousAddressOpen(false);
          setImportPreviousDismissed(true);
        }}
        primaryFirst
        presentation="saveChanges"
        actionStyle="application"
        size="wide"
        equalWidthActions
      />

      <ConfirmDialog
        isOpen={importPreviousEmploymentOpen}
        title="Import from Previous Application"
        message="We found some employment records from your previous application with us. Would you like us to import these?"
        primaryLabel="Yes, import as current employment"
        onPrimary={() => importPreviousEmployment("current")}
        neutralLabel="Yes, import as previous employment"
        onNeutral={() => importPreviousEmployment("previous")}
        secondaryLabel="No, thank you"
        onSecondary={dismissImportPreviousEmployment}
        onDismiss={dismissImportPreviousEmployment}
        overlayDataTestId="application-detail-confirm-import-previous-employment"
        primaryButtonDataTestId="application-detail-confirm-import-previous-employment-current-button"
        neutralButtonDataTestId="application-detail-confirm-import-previous-employment-previous-button"
        secondaryButtonDataTestId="application-detail-confirm-import-previous-employment-dismiss-button"
        closeButtonDataTestId="application-detail-confirm-import-previous-employment-close-button"
        primaryFirst
        presentation="saveChanges"
        actionStyle="application"
        size="wide"
        equalWidthActions
      />
    </>
  );
}
